// Decide whether a freshly-loaded /history snapshot reveals a chat turn that is
// still running on the server and should be re-attached to via the resume
// endpoint (FRONT-638).
//
// On reopen, /history reseeds the conversation. If the LAST row is an assistant
// turn left at status="streaming" (server marks it `streamStatus:"interrupted"`)
// AND it carries a `streamId`, the turn is resumable: the browser strips that
// trailing partial and drives `GET /api/v2/chat/stream/:streamId/resume` to
// replay + tail the live turn. Any other shape (final last row, a user row last,
// a streaming row with no streamId from the legacy single-insert path) returns
// null — nothing to resume, leave today's behaviour untouched.
//
// Kept pure + structural (no AI SDK types) so the trigger decision is
// unit-testable in isolation; the pursuit controller
// (resume-pursuit-controller.ts) layers the status, suppression, ownership and
// re-arm guards on top of this.

/**
 * Minimal structural shape of a /history message — only what the decision
 * needs. `metadata` is `unknown` to match the AI SDK's opaque UIMessage
 * metadata; we narrow it at runtime.
 */
export interface ResumableMessage {
  role: string;
  metadata?: unknown;
}

export interface ResumeTarget {
  streamId: string;
  /**
   * FRONT-2195: the SAME liveness rule the resume endpoint itself uses
   * (`src/agents/streams/liveness.ts`), passed through from `/history`'s
   * `streamLive` metadata field. `true` only when the server currently
   * reads this row's producer as still going *right now* — `false` for a
   * genuinely final turn and for an orphaned one the sweeper hasn't caught
   * yet, both of which otherwise carry the identical `streamStatus:
   * "interrupted"` shape. Consumers that need to tell "worth chasing
   * indefinitely" from "try a few times, then give up" key off this field,
   * never off `streamStatus` alone.
   */
  streamLive: boolean;
}

/**
 * Returns the resume target when the trailing history row is a still-running
 * assistant turn carrying a streamId, else null.
 */
export function resumeTargetFromHistory(
  history: readonly ResumableMessage[] | undefined,
): ResumeTarget | null {
  if (!history || history.length === 0) return null;
  const last = history[history.length - 1];
  if (!last || last.role !== 'assistant') return null;
  const meta = last.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const { streamStatus, streamId, streamLive } = meta as {
    streamStatus?: unknown;
    streamId?: unknown;
    streamLive?: unknown;
  };
  if (streamStatus !== 'interrupted') return null;
  if (typeof streamId !== 'string' || streamId.length === 0) return null;
  return { streamId, streamLive: streamLive === true };
}

/**
 * FRONT-765 (live-test finding, 2026-08-31): a deliberate Stop press also
 * lands in `onFinish` via the AI SDK's own `isAbort: true` — it calls
 * `onFinish` on ANY abort, not just a dropped connection. If the backend
 * turn hasn't yet honored the cooperative round-boundary cancel (still
 * mid-round — an in-flight `delegate_to_agent` call routinely takes tens of
 * seconds), `onFinish`'s own freshly-refetched /history snapshot still shows
 * the trailing row as `streamStatus: "interrupted"` with a live streamId —
 * exactly the shape `resumeTargetFromHistory` above treats as "a dropped
 * connection to reattach to" (FRONT-638). Left alone, Stop would trigger
 * that reattach on the SAME still-running stream: it replays the whole
 * turn's tape on top of what's already rendered (duplicated tool cards and
 * text) and flips the composer back to "Stop" mid-replay, so a Stop press
 * looks like it did nothing — or made things worse — even on a turn the
 * backend is about to correctly cancel.
 *
 * Returns the streamId to add to the pursuit controller's permanent
 * suppression set (resume-pursuit-controller.ts `suppress`) when this
 * `onFinish` call was BOTH an abort AND caused by a deliberate Stop press —
 * null otherwise (a normal completion, a genuinely dropped connection, or an
 * abort from something other than Stop should never suppress a legitimate
 * resume).
 */
export function resumeTargetToSuppress(params: {
  isAbort: boolean;
  wasDeliberateStop: boolean;
  history: readonly ResumableMessage[] | undefined;
}): string | null {
  if (!params.isAbort || !params.wasDeliberateStop) return null;
  return resumeTargetFromHistory(params.history)?.streamId ?? null;
}

/**
 * FRONT-2195 review F5: every streamId a Stop press must poison
 * SYNCHRONOUSLY, before anything async can re-attach. `resumeTargetToSuppress`
 * above only runs from `onFinish`, and `onFinish` only runs when a stream was
 * actually open — so a Stop pressed while a pursuit was backing off between
 * attempts, or while a re-arm was waiting, poisoned nothing, and the pending
 * timer re-attached to the turn the person had just stopped.
 *
 * The candidates are the pursuit's streamId (in flight or backing off), the
 * streamId a pending re-arm is waiting on, and the turn `/history` currently
 * shows as still running — any of them is "the turn on screen". Poisoning
 * one that turns out not to be running costs nothing: a finished turn is
 * never re-attached anyway.
 */
export function streamIdsToSuppressOnStop(params: {
  pursuitStreamId: string | null;
  scheduledReattachStreamId: string | null;
  history: readonly ResumableMessage[] | undefined;
}): string[] {
  const out = new Set<string>();
  if (params.pursuitStreamId) out.add(params.pursuitStreamId);
  if (params.scheduledReattachStreamId) out.add(params.scheduledReattachStreamId);
  const fromHistory = resumeTargetFromHistory(params.history)?.streamId;
  if (fromHistory) out.add(fromHistory);
  return [...out];
}
